/**
 * Shared API Mock Configuration System
 *
 * This utility allows any feature to easily switch between mock and real API responses.
 * Place this in utils so it can be shared across all features.
 *
 * @example Basic usage for any feature:
 * ```typescript
 * import { createFeatureApiConfig } from '@/utils/apiMockConfig';
 *
 * // Create config for your feature
 * export const dashboardApiConfig = createFeatureApiConfig('dashboard');
 *
 * // Use in your service
 * export const dashboardService = dashboardApiConfig.getCurrentMode()
 *   ? mockDashboardService
 *   : realDashboardService;
 * ```
 *
 * @example Advanced usage with custom options:
 * ```typescript
 * export const chartApiConfig = createFeatureApiConfig('chart', {
 *   envVarName: 'VITE_USE_MOCK_CHART_API',
 *   defaultUseMock: true,
 *   enableConsoleHelpers: true
 * });
 * ```
 *
 * @example Multiple features:
 * ```typescript
 * // In different feature files:
 * export const userApiConfig = createFeatureApiConfig('user');
 * export const analyticsApiConfig = createFeatureApiConfig('analytics');
 * export const notificationApiConfig = createFeatureApiConfig('notification');
 *
 * // Each gets their own:
 * // - Environment variable: VITE_USE_MOCK_USER_API, VITE_USE_MOCK_ANALYTICS_API, etc.
 * // - localStorage key: syncly-user-api-mock-mode, syncly-analytics-api-mock-mode, etc.
 * // - Console helpers: userApiConfig.enableMock(), analyticsApiConfig.enableMock(), etc.
 * ```
 */

import { isDevMode } from '@/utils/environment';

export interface ApiMockConfig {
  isMockMode(): boolean;
  setMockMode(useMock: boolean): void;
  clearRuntimeSetting(): void;
  getDebugInfo(): {
    mockMode: boolean;
    envSetting: string | undefined;
    localStorageValue: string | null;
    defaultMode: boolean;
  };
}

export interface ApiMockConfigOptions {
  /** Custom environment variable name. Defaults to VITE_USE_MOCK_{FEATURE}_API */
  envVarName?: string;
  /** Default mock mode when no environment variable is set. Defaults to false */
  defaultUseMock?: boolean;
  /** Enable console helper functions on window object. Defaults to true */
  enableConsoleHelpers?: boolean;
}

/**
 * Factory function to create feature-specific API mock configuration
 *
 * @param featureKey - Unique identifier for the feature (e.g., 'report', 'dashboard', 'user')
 * @param options - Configuration options
 * @returns ApiMockConfig instance for the feature
 */
export function createFeatureApiConfig(
  featureKey: string,
  options: ApiMockConfigOptions = {},
): ApiMockConfig {
  const isDev = isDevMode();
  const {
    envVarName = `VITE_USE_MOCK_${featureKey.toUpperCase()}_API`,
    defaultUseMock = false,
    enableConsoleHelpers = true,
  } = options;

  const localStorageKey = `syncly-${featureKey.toLowerCase()}-api-mock-mode`;
  const useMockFromEnv = import.meta.env[envVarName] === 'true';

  const config: ApiMockConfig = {
    isMockMode(): boolean {
      if (!isDev) return false;
      // Priority: localStorage > environment variable > default
      const localStorageValue = localStorage.getItem(localStorageKey);
      if (localStorageValue !== null) {
        return localStorageValue === 'true';
      }

      if (import.meta.env[envVarName] !== undefined) {
        return useMockFromEnv;
      }

      return defaultUseMock;
    },

    setMockMode(useMock: boolean): void {
      if (!isDev) return;
      localStorage.setItem(localStorageKey, useMock.toString());
      console.info(
        `[${featureKey}] API mock mode ${useMock ? 'enabled' : 'disabled'}`,
      );
    },

    clearRuntimeSetting(): void {
      if (!isDev) return;
      localStorage.removeItem(localStorageKey);
      console.info(
        `[${featureKey}] Runtime setting cleared, using environment/default`,
      );
    },

    getDebugInfo() {
      if (!isDev) throw new Error('Not in dev mode');
      return {
        mockMode: this.isMockMode(),
        envSetting: import.meta.env[envVarName],
        localStorageValue: localStorage.getItem(localStorageKey),
        defaultMode: defaultUseMock,
      };
    },
  };

  // Global console helpers for easy debugging
  if (enableConsoleHelpers && typeof window !== 'undefined') {
    const windowKey = `${featureKey.toLowerCase()}ApiConfig`;
    if (isDev) {
      (window as any)[windowKey] = {
        enableMock: () => config.setMockMode(true),
        disableMock: () => config.setMockMode(false),
        clearSetting: () => config.clearRuntimeSetting(),
        getInfo: () => config.getDebugInfo(),
      };
    }
  }

  return config;
}

/**
 * Utility to create a service factory that switches between mock and real implementations
 *
 * @example
 * ```typescript
 * const createUserService = createServiceFactory(
 *   userApiConfig,
 *   mockUserService,
 *   realUserService
 * );
 *
 * export const userService = createUserService();
 * ```
 */
export function createServiceFactory<T>(
  config: ApiMockConfig,
  mockService: T,
  realService: T,
): () => T {
  return () => (config.isMockMode() ? mockService : realService);
}

// Legacy exports for backward compatibility
export const createApiMockConfig = createFeatureApiConfig;
